
import React, { useEffect, useState } from 'react';
import './PendingPage.css';
import { subscriptionsAPI } from '../../services/api';
import SubscribeButtons from '../../components/SubscribeButtons/SubscribeButtons';

const RECURRING_TIERS = new Set(['monthly', 'quarterly', 'yearly']);

interface SubscriptionTier {
  id: string;
  name: string;
  price: number;
  features: string[];
  isPopular?: boolean;
}

interface PendingUser {
  email: string;
  displayName: string;
  selectedTier: string;
  paymentLink?: string;
  /** When true, render as a re-subscribe nudge instead of a first-time
   *  payment screen. Lets us reuse this page for both flows. */
  renewal?: boolean;
  /** ISO timestamp of the prior period end, used in the renewal copy. */
  periodEnd?: string | null;
}

interface PendingPageProps {
  user: PendingUser;
  subscriptionTiers: SubscriptionTier[];
  onResendEmail?: () => Promise<void>;
  onBack?: () => void;
  onCheckStatus?: () => Promise<boolean>;
}

const PendingPage: React.FC<PendingPageProps> = ({
  user,
  subscriptionTiers,
  onResendEmail,
  onBack,
  onCheckStatus
}) => {
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [checking, setChecking] = useState(false);
  const [renewLoading, setRenewLoading] = useState(false);
  const [renewError, setRenewError] = useState<string | null>(null);
  const [renewUrl, setRenewUrl] = useState<string | null>(null);
  const [subscribed, setSubscribed] = useState(false);
  const [activationPolling, setActivationPolling] = useState(false);

  const selectedTierInfo = subscriptionTiers.find(t => t.id === user.selectedTier);
  const useRecurring = RECURRING_TIERS.has(user.selectedTier);

  // For LIFETIME renewals (or other non-recurring tiers), auto-mint a
  // pending paypal.me-based subscription on mount. Recurring tiers
  // use SubscribeButtons instead and skip this effect.
  useEffect(() => {
    if (!user.renewal || useRecurring || renewUrl || renewLoading) return;
    let cancelled = false;
    setRenewLoading(true);
    subscriptionsAPI.renew(user.selectedTier)
      .then(r => {
        if (cancelled) return;
        if (r?.approvalUrl) setRenewUrl(r.approvalUrl);
        else setRenewError('Could not start renewal.');
      })
      .catch(e => {
        if (cancelled) return;
        console.error('[PendingPage] renew failed', e);
        setRenewError(e?.message || 'Could not start renewal.');
      })
      .finally(() => {
        if (!cancelled) setRenewLoading(false);
      });
    return () => { cancelled = true; };
  }, [user.renewal, user.selectedTier, useRecurring, renewUrl, renewLoading]);

  // Poll for activation after the user approves a recurring subscription.
  // The webhook flips status server-side; we just need to detect it.
  const startActivationPolling = () => {
    if (activationPolling) return;
    setActivationPolling(true);
    const started = Date.now();
    const poll = async () => {
      try {
        const res = await subscriptionsAPI.getMySubscription();
        if (res?.subscription?.status === 'active') {
          setSubscribed(true);
          setActivationPolling(false);
          // Give the toast a moment, then hand control back so the
          // SPA refreshes its auth state. Simplest: reload.
          setTimeout(() => window.location.reload(), 1500);
          return;
        }
      } catch (e) {
        console.warn('[PendingPage] activation poll failed', e);
      }
      if (Date.now() - started < 45_000) {
        window.setTimeout(poll, 2500);
      } else {
        setActivationPolling(false);
      }
    };
    poll();
  };

  const renewalPaymentLink = renewUrl || user.paymentLink || null;
  const formattedPeriodEnd = user.periodEnd ? (() => {
    const d = new Date(user.periodEnd!);
    return isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  })() : null;

  const handleResendEmail = async () => {
    if (!onResendEmail || resending) return;
    setResending(true);
    setResendSuccess(false);
    try {
      await onResendEmail();
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (error) {
      console.error('Failed to resend email:', error);
    } finally {
      setResending(false);
    }
  };

  const handleCheckStatus = async () => {
    if (!onCheckStatus || checking) return;
    setChecking(true);
    try {
      await onCheckStatus();
    } catch (error) {
      console.error('Failed to check status:', error);
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="pending-page">
      <div className="pending-container">

        {/* Logo */}
        <div className="pending-logo">
          <img src="https://jalenward.com/images/logosmall.png" alt="OnlyJalen" />
        </div>

        {/* Thank You / Welcome Back Header */}
        <div className="thankyou-header">
          <div className="thankyou-icon">{user.renewal ? '👋' : '🎉'}</div>
          <h1>
            {user.renewal
              ? `Welcome back, ${user.displayName.split(' ')[0]}!`
              : `Thank You, ${user.displayName.split(' ')[0]}!`}
          </h1>
          <p className="thankyou-subtitle">
            {user.renewal
              ? formattedPeriodEnd
                ? `Your subscription ended on ${formattedPeriodEnd}. Renew now to pick up where you left off.`
                : 'Your subscription has expired. Renew now to regain access.'
              : 'Your account has been created successfully.'}
          </p>
        </div>

        {/* Main Card */}
        <div className="pending-card">

          {/* Step Tracker */}
          {!user.renewal && (
            <div className="step-indicator">
              <div className="step completed">
                <span className="step-number">✓</span>
                <span className="step-label">Account Created</span>
              </div>
              <div className="step-line" />
              <div className="step active">
                <span className="step-number">2</span>
                <span className="step-label">Check Email</span>
              </div>
              <div className="step-line" />
              <div className="step">
                <span className="step-number">3</span>
                <span className="step-label">Get Access</span>
              </div>
            </div>
          )}

          {/* Email/Payment CTA — the main message */}
          <div className="email-cta-block">
            <div className="email-icon-wrap">
              {user.renewal ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M23 4v6h-6"/>
                  <path d="M1 20v-6h6"/>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"/>
                  <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"/>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              )}
            </div>
            <h2>
              {subscribed
                ? '🎉 Welcome to OnlyJalen!'
                : user.renewal
                  ? useRecurring ? 'Restart Your Subscription' : 'Renew Your Subscription'
                  : useRecurring ? 'Subscribe to OnlyJalen' : 'Check Your Email to Continue'}
            </h2>
            {subscribed ? (
              <p className="email-instruction">
                Your subscription is active. Loading your account…
              </p>
            ) : useRecurring ? (
              <p className="email-instruction">
                {user.renewal
                  ? `Subscribe to auto-renew at ${selectedTierInfo ? `$${selectedTierInfo.price}` : 'your old price'} — no more renewal emails, PayPal handles each billing cycle.`
                  : `Click below to subscribe with PayPal. You'll be billed automatically at the start of each new cycle until you cancel.`}
              </p>
            ) : user.renewal ? (
              <p className="email-instruction">
                Click below to pay and reactivate your subscription. New period starts the moment your payment clears.
              </p>
            ) : (
              <>
                <p>
                  We sent a payment link to<br />
                  <strong>{user.email}</strong>
                </p>
                <p className="email-instruction">
                  Open that email and complete your payment to activate your subscription and get full access to OnlyJalen.
                </p>
              </>
            )}

            {/* Recurring tiers: Smart Buttons. */}
            {useRecurring && !subscribed && (
              <SubscribeButtons
                planType={user.selectedTier as 'monthly' | 'quarterly' | 'yearly'}
                email={user.email}
                displayName={user.displayName}
                onApproved={() => startActivationPolling()}
                onError={(e) => console.error('[PendingPage] subscribe error', e)}
              />
            )}

            {/* While polling for webhook-driven activation. */}
            {activationPolling && !subscribed && (
              <div className="renew-status">
                <span className="spinner" /> Activating your subscription with PayPal…
              </div>
            )}

            {/* Lifetime renewals + legacy renewal flow: paypal.me path. */}
            {!useRecurring && renewLoading && user.renewal && (
              <div className="renew-status">
                <span className="spinner" /> Preparing your renewal link…
              </div>
            )}

            {!useRecurring && renewError && (
              <div className="renew-error">{renewError}</div>
            )}

            {!useRecurring && renewalPaymentLink && (
              <a
                href={renewalPaymentLink}
                target="_blank"
                rel="noopener noreferrer"
                className="paypal-pay-btn"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788l.038-.2.728-4.616.047-.254c.072-.454.462-.788.922-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.773-4.471z"/>
                </svg>
                {user.renewal ? 'Renew with PayPal' : 'Pay Now with PayPal'}
                {selectedTierInfo && <span className="pay-amount">${selectedTierInfo.price}</span>}
              </a>
            )}
          </div>

          {/* Selected Plan Summary */}
          {selectedTierInfo && (
            <div className="selected-tier-card">
              <div className="tier-header">
                <span className="tier-label">Your Selected Plan</span>
                {selectedTierInfo.isPopular && <span className="popular-tag">Popular</span>}
              </div>
              <div className="tier-details">
                <span className="tier-name">{selectedTierInfo.name}</span>
                <span className="tier-price">${selectedTierInfo.price}</span>
              </div>
            </div>
          )}

          {/* Resend Email */}
          <div className="resend-section">
            <p className="resend-note">Didn't get the email? Check your spam folder or resend it.</p>
            <button
              className="resend-btn"
              onClick={handleResendEmail}
              disabled={resending}
            >
              {resending ? (
                <span className="spinner" />
              ) : resendSuccess ? (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20,6 9,17 4,12"/>
                  </svg>
                  Email Sent!
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  Resend Payment Email
                </>
              )}
            </button>
          </div>

          {/* Check Status */}
          <div className="status-section">
            <button
              className="check-status-btn"
              onClick={handleCheckStatus}
              disabled={checking}
            >
              {checking ? (
                <span className="spinner" />
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M23 4v6h-6M1 20v-6h6"/>
                    <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
                  </svg>
                  I've Paid — Check My Access
                </>
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="pending-footer">
          <p>Questions? Email us at <a href="mailto:support@onlyjalen.com" className="support-link">support@onlyjalen.com</a></p>
          {onBack && (
            <button className="back-btn" onClick={onBack}>
              ← Back to sign in
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

export default PendingPage;
